import { useEffect, useMemo, useState } from 'react'

import type { ProductTypeDto } from '../api/productTypes'
import { FilterCombobox } from './FilterCombobox'
import {
  effectiveProductTypeIdFromCascade,
  findProductTypeByName,
  isL0ProductType,
  listChildProductTypes,
  resolveProductTypeCascade,
} from '../screens/catalogFilters'

type ProductTypeCascadePickerProps = {
  productTypes: ProductTypeDto[]
  value: number | null
  onChange: (productTypeId: number | null) => void
  className?: string
  quickSearchClassName?: string
  compact?: boolean
  allowEmpty?: boolean
  emptyLabel?: string
  disabled?: boolean
  idPrefix?: string
  /** When false, renders only L0–L3 cascade (for collapsible «Ещё фильтры»). Default true. */
  showQuickSearch?: boolean
  /** When false, renders only quick search field (for primary filter row). Default true. */
  showCascadeLevels?: boolean
}

export const ProductTypeCascadePicker = ({
  productTypes,
  value,
  onChange,
  className,
  quickSearchClassName,
  compact = false,
  allowEmpty = true,
  emptyLabel = '— не назначена —',
  disabled = false,
  idPrefix: _idPrefix,
  showQuickSearch = true,
  showCascadeLevels = true,
}: ProductTypeCascadePickerProps) => {
  const [quickQuery, setQuickQuery] = useState('')
  const [l0Query, setL0Query] = useState('')
  const [l1Query, setL1Query] = useState('')
  const [l2Query, setL2Query] = useState('')
  const [l3Query, setL3Query] = useState('')
  const [l0Id, setL0Id] = useState<number | null>(null)
  const [l1Id, setL1Id] = useState<number | null>(null)
  const [l2Id, setL2Id] = useState<number | null>(null)

  useEffect(() => {
    const resolved = resolveProductTypeCascade(value, productTypes)
    setL0Id(resolved.l0Id)
    setL1Id(resolved.l1Id)
    setL2Id(resolved.l2Id)

    const l0Type = productTypes.find((type) => type.id === resolved.l0Id) ?? null
    const l1Type = productTypes.find((type) => type.id === resolved.l1Id) ?? null
    const l2Type = productTypes.find((type) => type.id === resolved.l2Id) ?? null
    const l3Type = productTypes.find((type) => type.id === resolved.l3Id) ?? null
    setL0Query(l0Type?.name ?? '')
    setL1Query(l1Type?.name ?? '')
    setL2Query(l2Type?.name ?? '')
    setL3Query(l3Type?.name ?? '')
    // Быстрый ввод показывает самый глубокий выбранный уровень.
    setQuickQuery((l3Type ?? l2Type ?? l1Type ?? l0Type)?.name ?? '')
  }, [productTypes, value])

  // Единый поиск по всей таксономии: можно сразу вписать «карбонад» / «вырезка»,
  // не заполняя L0→L1→L2 по очереди — путь расставится сам из выбранного узла.
  const searchableTypes = useMemo(
    () => productTypes.filter((type) => type.active !== false && Boolean(type.code?.trim())),
    [productTypes],
  )
  const searchableNames = useMemo(() => {
    const seen = new Set<string>()
    const names: string[] = []
    for (const type of searchableTypes) {
      if (!seen.has(type.name)) {
        seen.add(type.name)
        names.push(type.name)
      }
    }
    return names
  }, [searchableTypes])

  const productTypesL0 = useMemo(
    () => productTypes.filter(isL0ProductType),
    [productTypes],
  )

  const selectedL0 = useMemo(
    () => productTypesL0.find((type) => type.id === l0Id) ?? null,
    [l0Id, productTypesL0],
  )

  const productTypesL1 = useMemo(
    () => (selectedL0 ? listChildProductTypes(selectedL0, productTypes, 1) : []),
    [productTypes, selectedL0],
  )

  const selectedL1 = useMemo(
    () => productTypesL1.find((type) => type.id === l1Id) ?? null,
    [l1Id, productTypesL1],
  )

  const productTypesL2 = useMemo(
    () => (selectedL1 ? listChildProductTypes(selectedL1, productTypes, 2) : []),
    [productTypes, selectedL1],
  )

  const selectedL2 = useMemo(
    () => productTypesL2.find((type) => type.id === l2Id) ?? null,
    [l2Id, productTypesL2],
  )

  const productTypesL3 = useMemo(
    () =>
      selectedL2
        ? productTypes
            .filter(
              (type) =>
                type.code.startsWith(`${selectedL2.code}.`) &&
                type.code.split('.').length - 1 >= 3,
            )
            .sort((left, right) => left.name.localeCompare(right.name, 'ru'))
        : [],
    [productTypes, selectedL2],
  )

  const emitChange = (next: {
    l0Id: number | null
    l1Id: number | null
    l2Id: number | null
    l3Id: number | null
  }) => {
    setL0Id(next.l0Id)
    setL1Id(next.l1Id)
    setL2Id(next.l2Id)
    onChange(effectiveProductTypeIdFromCascade(next))
  }

  const fieldClass = compact
    ? 'market-filters__field market-filters__field--sm'
    : undefined

  const l0Label = compact ? 'Отдел' : 'Отдел закупки'
  const l3Label = compact ? 'Уточнение' : 'Уточнение (L3)'
  const l0Placeholder = allowEmpty ? emptyLabel : l0Label
  const l1Placeholder = selectedL0 ? 'Уточните категорию' : 'Сначала отдел'
  const l2Placeholder = selectedL1 ? 'Уточните подкатегорию' : 'Сначала категорию'
  const l3Placeholder =
    selectedL2
      ? productTypesL3.length > 0
        ? 'Уточните позицию'
        : 'Нет уточнений L3'
      : 'Сначала подкатегорию'

  const quickSearchField = (
    <FilterCombobox
      className={quickSearchClassName ?? fieldClass}
      label="Быстрый поиск подкатегории"
      value={quickQuery}
      options={searchableNames}
      placeholder="Например: карбонад, вырезка"
      disabled={disabled}
      listAllOnFocus
      maxOptions={200}
      onChange={(query) => {
        setQuickQuery(query)
        const match = findProductTypeByName(searchableTypes, query)
        if (match) {
          // value обновится → useEffect расставит L0–L3 из выбранного узла.
          onChange(match.id)
        } else if (query.trim() === '' && allowEmpty) {
          onChange(null)
        }
      }}
    />
  )

  const cascadeLevels = (
    <>
      <FilterCombobox
        className={fieldClass}
        label={l0Label}
        value={l0Query}
        options={productTypesL0.map((type) => type.name)}
        placeholder={l0Placeholder}
        disabled={disabled}
        listAllOnFocus
        maxOptions={120}
        onChange={(query) => {
          const match = findProductTypeByName(productTypesL0, query)
          setL0Query(query)
          setL1Query('')
          setL2Query('')
          setL3Query('')
          emitChange({ l0Id: match?.id ?? null, l1Id: null, l2Id: null, l3Id: null })
        }}
      />

      <FilterCombobox
        className={fieldClass}
        label="Категория"
        value={l1Query}
        options={productTypesL1.map((type) => type.name)}
        placeholder={l1Placeholder}
        disabled={disabled || !selectedL0}
        listAllOnFocus
        maxOptions={120}
        onChange={(query) => {
          const match = findProductTypeByName(productTypesL1, query)
          setL1Query(query)
          setL2Query('')
          setL3Query('')
          emitChange({ l0Id, l1Id: match?.id ?? null, l2Id: null, l3Id: null })
        }}
      />

      <FilterCombobox
        className={fieldClass}
        label="Подкатегория"
        value={l2Query}
        options={productTypesL2.map((type) => type.name)}
        placeholder={l2Placeholder}
        disabled={disabled || !selectedL1}
        listAllOnFocus
        maxOptions={120}
        onChange={(query) => {
          const match = findProductTypeByName(productTypesL2, query)
          setL2Query(query)
          setL3Query('')
          emitChange({ l0Id, l1Id, l2Id: match?.id ?? null, l3Id: null })
        }}
      />

      <FilterCombobox
        className={fieldClass}
        label={l3Label}
        value={l3Query}
        options={productTypesL3.map((type) => type.name)}
        placeholder={l3Placeholder}
        disabled={disabled || !selectedL2 || productTypesL3.length === 0}
        listAllOnFocus
        maxOptions={120}
        onChange={(query) => {
          const match = findProductTypeByName(productTypesL3, query)
          setL3Query(query)
          emitChange({ l0Id, l1Id, l2Id, l3Id: match?.id ?? null })
        }}
      />
    </>
  )

  if (showQuickSearch && !showCascadeLevels) {
    return quickSearchField
  }

  if (!showQuickSearch && showCascadeLevels) {
    return <div className={className ?? 'market-product-type-cascade'}>{cascadeLevels}</div>
  }

  return (
    <div className={className ?? 'market-product-type-cascade'}>
      {showQuickSearch ? quickSearchField : null}
      {showCascadeLevels ? cascadeLevels : null}
    </div>
  )
}
